import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { Send, Search, X, Users, Music, Calendar } from 'lucide-react';
import './CustomEmails.css';

const CustomEmails = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [samples, setSamples] = useState([]);
  const [genres, setGenres] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Email type state
  const [emailType, setEmailType] = useState('custom'); // 'custom' or 'prize'
  
  // Filter states
  const [genreScoreThreshold, setGenreScoreThreshold] = useState(0);
  const [selectedGenre, setSelectedGenre] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [minDownloads, setMinDownloads] = useState(0);
  const [subscriberSearch, setSubscriberSearch] = useState('');
  const [sampleSearch, setSampleSearch] = useState('');
  
  // Selection states
  const [selectedSubscribers, setSelectedSubscribers] = useState([]);
  const [selectedSamples, setSelectedSamples] = useState([]);
  const [customExpireHours, setCustomExpireHours] = useState(48);
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showSubscriberList, setShowSubscriberList] = useState(false);
  const [showSampleList, setShowSampleList] = useState(false);
  const [filteredSubscribers, setFilteredSubscribers] = useState([]);
  const [filteredSamples, setFilteredSamples] = useState([]);

  useEffect(() => {
    fetchGenres();
    fetchCategories();
    fetchAllSubscribers();
    fetchAllSamples();
  }, []);

  const applySubscriberFilters = useCallback(() => {
    let filtered = [...subscribers];

    // Filter by search
    if (subscriberSearch) {
      const search = subscriberSearch.toLowerCase();
      filtered = filtered.filter(sub =>
        sub.email.toLowerCase().includes(search) ||
        (sub.firstName && sub.firstName.toLowerCase().includes(search)) ||
        (sub.lastName && sub.lastName.toLowerCase().includes(search))
      );
    }

    // Filter by genre
    if (selectedGenre) {
      filtered = filtered.filter(sub =>
        sub.preferredGenres && sub.preferredGenres.some(g => g._id === selectedGenre)
      );
    }

    // Filter by category (has downloads in this category)
    if (selectedCategory) {
      filtered = filtered.filter(sub =>
        sub.categoryScore && sub.categoryScore.get && sub.categoryScore.get(selectedCategory) > 0
      );
    }

    // Filter by min downloads
    if (minDownloads > 0) {
      filtered = filtered.filter(sub => (sub.totalDownloads || 0) >= minDownloads);
    }

    // Filter by genre score threshold
    if (genreScoreThreshold > 0) {
      filtered = filtered.filter(sub => {
        if (!sub.genreScore) return false;
        const scores = Array.from(sub.genreScore.values());
        return scores.some(score => score >= genreScoreThreshold);
      });
    }

    setFilteredSubscribers(filtered);
  }, [subscribers, genreScoreThreshold, selectedGenre, selectedCategory, minDownloads, subscriberSearch]);

  const applySampleFilters = useCallback(() => {
    let filtered = [...samples];

    if (sampleSearch) {
      const search = sampleSearch.toLowerCase();
      filtered = filtered.filter(sample =>
        sample.title.toLowerCase().includes(search) ||
        (sample.description && sample.description.toLowerCase().includes(search)) ||
        (sample.filename && sample.filename.toLowerCase().includes(search))
      );
    }

    setFilteredSamples(filtered);
  }, [samples, sampleSearch]);

  useEffect(() => {
    applySubscriberFilters();
  }, [applySubscriberFilters]);

  useEffect(() => {
    applySampleFilters();
  }, [applySampleFilters]);

  const fetchGenres = async () => {
    try {
      const response = await api.get('/genres');
      setGenres(response.data.genres || []);
    } catch (error) {
      console.error('Error fetching genres:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchAllSubscribers = async () => {
    try {
      const response = await api.get('/subscribers?limit=1000');
      setSubscribers(response.data.subscribers || []);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
    }
  };

  const fetchAllSamples = async () => {
    try {
      const response = await api.get('/samples?limit=1000');
      setSamples(response.data.samples || []);
    } catch (error) {
      console.error('Error fetching samples:', error);
    }
  };


  const toggleSubscriber = (subscriber) => {
    const isSelected = selectedSubscribers.some(s => s._id === subscriber._id);
    if (isSelected) {
      setSelectedSubscribers(selectedSubscribers.filter(s => s._id !== subscriber._id));
    } else {
      setSelectedSubscribers([...selectedSubscribers, subscriber]);
    }
  };

  const toggleSample = (sample) => {
    const isSelected = selectedSamples.some(s => s._id === sample._id);
    if (isSelected) {
      setSelectedSamples(selectedSamples.filter(s => s._id !== sample._id));
    } else {
      setSelectedSamples([...selectedSamples, sample]);
    }
  };

  const selectAllFiltered = () => {
    const newSelections = [...selectedSubscribers];
    filteredSubscribers.forEach(sub => {
      if (!newSelections.some(s => s._id === sub._id)) {
        newSelections.push(sub);
      }
    });
    setSelectedSubscribers(newSelections);
    setMessage(`Selected ${filteredSubscribers.length} subscribers`);
    setTimeout(() => setMessage(''), 3000);
  };

  const clearAllSubscribers = () => {
    setSelectedSubscribers([]);
  };

  const selectAllFilteredSamples = () => {
    const newSelections = [...selectedSamples];
    filteredSamples.forEach(sample => {
      if (!newSelections.some(s => s._id === sample._id)) {
        newSelections.push(sample);
      }
    });
    setSelectedSamples(newSelections);
  };

  const clearAllSamples = () => {
    setSelectedSamples([]);
  };

  const handleSendCustomEmails = async () => {
    if (selectedSubscribers.length === 0) {
      setMessage('Please select at least one subscriber');
      return;
    }

    if (selectedSamples.length === 0) {
      setMessage('Please select at least one sample');
      return;
    }

    if (!window.confirm(`Send ${selectedSamples.length} sample(s) to ${selectedSubscribers.length} subscriber(s)?`)) {
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const response = await api.post('/email/send-custom', {
        subscriberIds: selectedSubscribers.map(s => s._id),
        sampleIds: selectedSamples.map(s => s._id),
        expiryHours: customExpireHours
      });

      setMessage(`✓ Successfully sent ${response.data.totalSent} emails!`);
      
      // Reset selections after successful send
      setSelectedSubscribers([]);
      setSelectedSamples([]);
      
      // Refresh data
      fetchAllSubscribers();
      fetchAllSamples();
    } catch (error) {
      console.error('Error sending custom emails:', error);
      setMessage('✗ Error sending emails: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSendPrizeEmails = async () => {
    if (selectedSubscribers.length === 0) {
      setMessage('Please select at least one subscriber');
      return;
    }

    if (!window.confirm(`Send prize email to ${selectedSubscribers.length} subscriber(s)?`)) {
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const response = await api.post('/email/send-prize', {
        subscriberIds: selectedSubscribers.map(s => s._id)
      });

      setMessage(`✓ Successfully sent ${response.data.totalSent} prize emails!`);
      
      // Reset selections after successful send
      setSelectedSubscribers([]);
      
      // Refresh data
      fetchAllSubscribers();
    } catch (error) {
      console.error('Error sending prize emails:', error);
      setMessage('✗ Error sending prize emails: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="custom-emails-page">
      <div className="page-header">
        <div>
          <h2>{emailType === 'custom' ? 'Custom Email Campaign' : 'Prize Email Campaign'}</h2>
          <p>{emailType === 'custom' ? 'Send custom samples to selected subscribers' : 'Send prize emails to selected subscribers'}</p>
        </div>
      </div>

      {/* Email Type Switch */}
      <div className="email-type-switch">
        <button 
          className={`switch-btn ${emailType === 'custom' ? 'active' : ''}`}
          onClick={() => setEmailType('custom')}
        >
          Custom Emails
        </button>
        <button 
          className={`switch-btn ${emailType === 'prize' ? 'active' : ''}`}
          onClick={() => setEmailType('prize')}
        >
          Prize Emails
        </button>
      </div>

      {message && (
        <div className={`message ${message.includes('✓') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <div className="campaign-container">
        {/* Subscriber Selection Section */}
        <div className="selection-section">
          <div className="section-header">
            <div className="header-title">
              <Users size={20} />
              <h3>Select Subscribers</h3>
              <span className="badge">{selectedSubscribers.length} selected</span>
            </div>
            <button 
              className="toggle-list-btn"
              onClick={() => setShowSubscriberList(!showSubscriberList)}
            >
              {showSubscriberList ? 'Hide' : 'Show'} List
            </button>
          </div>

          <div className="filters-panel">
            <div className="filter-row">
              <div className="filter-group">
                <label>Search Subscribers</label>
                <div className="search-input">
                  <Search size={16} />
                  <input
                    type="text"
                    placeholder="Search by email or name..."
                    value={subscriberSearch}
                    onChange={(e) => setSubscriberSearch(e.target.value)}
                  />
                </div>
              </div>

              <div className="filter-group">
                <label>Genre</label>
                <select value={selectedGenre} onChange={(e) => setSelectedGenre(e.target.value)}>
                  <option value="">All Genres</option>
                  {genres.map(genre => (
                    <option key={genre._id} value={genre._id}>{genre.name}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Genre Score: {genreScoreThreshold}%</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={genreScoreThreshold}
                  onChange={(e) => setGenreScoreThreshold(Number(e.target.value))}
                  disabled={!selectedGenre}
                />
              </div>
            </div>

            <div className="filter-row">
              <div className="filter-group">
                <label>Category</label>
                <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat._id} value={cat._id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Min Downloads</label>
                <input
                  type="number"
                  min="0"
                  value={minDownloads}
                  onChange={(e) => setMinDownloads(Number(e.target.value))}
                />
              </div>

              <div className="filter-group">
                <label>&nbsp;</label>
                <button className="select-all-btn" onClick={selectAllFiltered}>
                  Select All Filtered ({filteredSubscribers.length})
                </button>
              </div>
            </div>
          </div>

          {showSubscriberList && (
            <div className="items-list">
              <div className="list-header">
                <span>{filteredSubscribers.length} filtered subscribers</span>
                {selectedSubscribers.length > 0 && (
                  <button className="clear-btn" onClick={clearAllSubscribers}>
                    <X size={16} /> Clear All
                  </button>
                )}
              </div>
              <div className="items-grid">
                {filteredSubscribers.map(subscriber => (
                  <div
                    key={subscriber._id}
                    className={`item-card ${selectedSubscribers.some(s => s._id === subscriber._id) ? 'selected' : ''}`}
                    onClick={() => toggleSubscriber(subscriber)}
                  >
                    <div className="item-info">
                      <strong>{subscriber.email}</strong>
                      {(subscriber.firstName || subscriber.lastName) && (
                        <small>{subscriber.firstName} {subscriber.lastName}</small>
                      )}
                      <div className="item-stats">
                        <span>⬇️ {subscriber.totalDownloads || 0}</span>
                        <span>📧 {subscriber.totalEmailsSent || 0}</span>
                      </div>
                    </div>
                    <div className="item-checkbox">
                      {selectedSubscribers.some(s => s._id === subscriber._id) && '✓'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedSubscribers.length > 0 && (
            <div className="selected-preview">
              <strong>Selected Subscribers ({selectedSubscribers.length}):</strong>
              <div className="selected-chips">
                {selectedSubscribers.map(sub => (
                  <span key={sub._id} className="chip">
                    {sub.email}
                    <X size={14} onClick={() => toggleSubscriber(sub)} />
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sample Selection Section - Only show for custom emails */}
        {emailType === 'custom' && (
          <div className="selection-section">
            <div className="section-header">
              <div className="header-title">
                <Music size={20} />
                <h3>Select Samples</h3>
                <span className="badge">{selectedSamples.length} selected</span>
              </div>
              <button 
                className="toggle-list-btn"
                onClick={() => setShowSampleList(!showSampleList)}
              >
                {showSampleList ? 'Hide' : 'Show'} List
              </button>
            </div>

            <div className="filters-panel">
              <div className="filter-row">
                <div className="filter-group">
                  <label>Search Samples</label>
                  <div className="search-input">
                    <Search size={16} />
                    <input
                      type="text"
                      placeholder="Search by title or filename..."
                      value={sampleSearch}
                      onChange={(e) => setSampleSearch(e.target.value)}
                    />
                  </div>
                </div>

                <div className="filter-group">
                  <label>&nbsp;</label>
                  <button className="select-all-btn" onClick={selectAllFilteredSamples}>
                    Select All Filtered ({filteredSamples.length})
                  </button>
                </div>

                {selectedSamples.length > 0 && (
                  <div className="filter-group">
                    <label>&nbsp;</label>
                    <button className="clear-btn" onClick={clearAllSamples}>
                      <X size={16} /> Clear All
                    </button>
                  </div>
                )}
              </div>
            </div>

            {showSampleList && (
              <div className="items-list">
                <div className="list-header">
                  <span>{filteredSamples.length} samples available</span>
                </div>
                <div className="items-grid">
                  {filteredSamples.map(sample => (
                    <div
                      key={sample._id}
                      className={`item-card ${selectedSamples.some(s => s._id === sample._id) ? 'selected' : ''}`}
                      onClick={() => toggleSample(sample)}
                    >
                      <div className="item-info">
                        <strong>{sample.title}</strong>
                        <small>{sample.fileName}</small>
                        <div className="item-stats">
                          {sample.category && <span className="cat-badge">{sample.category.name}</span>}
                          {sample.genre && <span className="gen-badge">{sample.genre.name}</span>}
                        </div>
                      </div>
                      <div className="item-checkbox">
                        {selectedSamples.some(s => s._id === sample._id) && '✓'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedSamples.length > 0 && (
              <div className="selected-preview">
                <strong>Selected Samples ({selectedSamples.length}):</strong>
                <div className="selected-chips">
                  {selectedSamples.map(sample => (
                    <span key={sample._id} className="chip">
                      {sample.title}
                      <X size={14} onClick={() => toggleSample(sample)} />
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Send Configuration */}
        <div className="send-section">
          <div className="section-header">
            <div className="header-title">
              <Calendar size={20} />
              <h3>{emailType === 'custom' ? 'Campaign Settings' : 'Prize Email Settings'}</h3>
            </div>
          </div>

          <div className="send-config">
            {emailType === 'custom' && (
              <>
                <div className="config-group">
                  <label>Link Expiration Time</label>
                  <div className="time-selector">
                    <input
                      type="number"
                      min="1"
                      max="168"
                      value={customExpireHours}
                      onChange={(e) => setCustomExpireHours(Number(e.target.value))}
                    />
                    <span>hours</span>
                  </div>
                  <small>{customExpireHours} hours = {(customExpireHours / 24).toFixed(1)} days</small>
                </div>

                <div className="campaign-summary">
                  <h4>Campaign Summary</h4>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <span className="summary-label">Recipients:</span>
                      <span className="summary-value">{selectedSubscribers.length}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Samples Per Email:</span>
                      <span className="summary-value">{selectedSamples.length}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Total Emails:</span>
                      <span className="summary-value highlight">
                        {selectedSubscribers.length}
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Expires In:</span>
                      <span className="summary-value">{customExpireHours}h</span>
                    </div>
                  </div>
                  <p style={{ marginTop: '16px', fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                    Each subscriber will receive ONE email containing all {selectedSamples.length} sample(s).
                  </p>
                </div>
              </>
            )}

            {emailType === 'prize' && (
              <div className="campaign-summary">
                <h4>Prize Email Summary</h4>
                <div className="summary-grid">
                  <div className="summary-item">
                    <span className="summary-label">Recipients:</span>
                    <span className="summary-value highlight">{selectedSubscribers.length}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Total Emails:</span>
                    <span className="summary-value highlight">
                      {selectedSubscribers.length}
                    </span>
                  </div>
                </div>
                <p style={{ marginTop: '16px', fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                  Each subscriber will receive a prize email using the prize template.
                </p>
              </div>
            )}

            <button
              className="send-campaign-btn"
              onClick={emailType === 'custom' ? handleSendCustomEmails : handleSendPrizeEmails}
              disabled={loading || selectedSubscribers.length === 0 || (emailType === 'custom' && selectedSamples.length === 0)}
            >
              {loading ? (
                <>Sending...</>
              ) : (
                <>
                  <Send size={20} />
                  {emailType === 'custom' 
                    ? `Send Campaign (${selectedSubscribers.length} email${selectedSubscribers.length !== 1 ? 's' : ''})`
                    : `Send Prize Emails (${selectedSubscribers.length} email${selectedSubscribers.length !== 1 ? 's' : ''})`
                  }
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomEmails;

